import classnames from 'classnames';
import React, { DetailedHTMLProps, HTMLAttributes, useRef } from 'react';
import { TransactionV2 } from '../transactions/transaction/transactionV2';
import { Transaction } from '../transactions/transaction/transaction';
import ErrorSubmission from './error-submission';
import { Conditional } from '@codametrix/ui-components';
import {
  appServiceLines,
  cmxDateTime,
  commonEnums,
  AmplifyCore
} from '@codametrix/ui-common';
import { ProcessStatus } from '../process-status/process-status';
import {
  LoadingSlugs,
  InteractiveTabContainer,
  CMXIcon
} from '@codametrix/ui-components';
import InvoiceMetadatav2 from './invoice-metadata/invoice-metadatav2';
import { InvoiceMetadataFormFields } from './invoice-metadata/invoice-metadata-form-fixtures';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import InvoiceCard from './invoice-components/invoice-card';
import { shouldShowResults } from '@codametrix/ui-components';
import { SuggestedLookup } from './lookup/lookupV2';
import { FieldDefinitions } from '@codametrix/ui-components';

const { CodeSearchType } = commonEnums;
const { DEFAULT_SUGGESTED_CODES } = AmplifyCore.state;

type CodeBenchPanelProps = {
  editable?: boolean;
  showProcessStatus?: boolean;
  benchLoading?: boolean;
  benchEditable?: Accelerate.BenchEditableState;
  caseDetailProps?: any;
  codableInvoices?: CMxCommonApp.CodableInvoice[];
  processInstance?: AmplifyAPI.ProcessInstance;
  onCodeUpdate?: CMxCommonApp.CodeChangeHandler;
  onCodeRemove?: CMxCommonApp.CodeChangeHandler;
  onTimeBasedCodeAdd?: (
    changeDetail: CMxCommonApp.CodeChange,
    time: string,
    age: string
  ) => void;
  onTimeBasedCodeUpdate?: (
    changeDetail: CMxCommonApp.CodeChange,
    time: string,
    age: string
  ) => void;
  onCodeSearchDrop?: CMxCommonApp.CodeChangeHandler;
  onCodeSearch?: CMxCommonApp.CodeSearchHandler;
  onCodeSearchSelection?: CMxCommonApp.CodeSearchSelectionHandler;
  handleAddAllCodes: (searchSelection: Accelerate.CodesAddCodeBench) => void;
  handleRemoveAllCodes: (codeChange: Accelerate.CodeChange) => void;
  createTransactionAndAddCode?: CMxCommonApp.CodeSearchSelectionHandler;
  onFeatureSelection?: (selection: CMxCommonApp.ChargeSessionSelection) => void;
  onInvoiceMetaEdit?: (invoice: AmplifyAPI.Invoice) => void;
  onChargeSessionAction?: CMxCommonApp.TransactionActionHandler;
  onTransactionSharedDiagnosisChange?: CMxCommonApp.TransactionSharedCodeHandler;
  handleEnableTechPro: (
    transactionCodeChange: CMxCommonApp.TechCodableTransaction
  ) => void;
  handleDisableTechPro: (
    transactionCodeChange: CMxCommonApp.TechCodableTransaction
  ) => void;
  onInvoiceRemove?: (
    invoiceDeleteParams: Accelerate.InvoiceDeleteParams
  ) => void;
  openChildWindow?: (windowOpenArgs: CMxCommonApp.WindowOpenArguments) => void;
  handleInvoiceAddition: () => void;
  panelActivations?: CMxCommonApp.PanelActivations;
  searchResults?: {
    [key: string]: CMxCommonApp.CodeInfo[];
  };
  codeSearchResultsV2?: Accelerate.CodeSearchResultMap;
  codeEdits: CMxCommonApp.CodeEditInputs;
  customClaimsRelatedCases?: CMxAPI.CaseRun[];
  customClaimsRelatedCasesLoadState?: CMxCommonApp.Loading;
  summarySection?: JSX.Element;
  transactionV2?: boolean;
  suggestedCodes?: CMxCommonApp.SuggestedCodes;
  onTransactionOrderChange?: (
    transactionReorderDetail: CMxCommonApp.TransactionReorderDetail
  ) => void;
} & CMxCommonApp.ChargeSessionsMeta &
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type CodeBenchPanelState = {
  editState: {
    add: boolean;
  };
};

class CodeBenchPanelv2 extends React.Component<
  CodeBenchPanelProps,
  CodeBenchPanelState
> {
  tabContainerRef = React.createRef<HTMLDivElement>();

  static displayName = 'CodeBenchPanel';

  static defaultProps = {
    onCodeUpdate: (changeDetail: CMxCommonApp.CodeChange) => {},
    onCodeRemove: (changeDetail: CMxCommonApp.CodeChange) => {},
    onTimeBasedCodeAdd: (
      changeDetail: CMxCommonApp.CodeChange,
      time: string,
      age: string
    ) => {},
    onTimeBasedCodeUpdate: (
      changeDetail: CMxCommonApp.CodeChange,
      time: string,
      age: string
    ) => {},
    onCodeSearchDrop: (changeDetail: CMxCommonApp.CodeChange) => {},
    onCodeSearch: (codeQuery: CMxCommonApp.CodeQuery) => {},
    onCodeSearchSelection: (searchSelection: CMxCommonApp.SearchResult) => {},
    handleAddAllCodes: (searchSelection: Accelerate.CodesAddCodeBench) => {},
    handleRemoveAllCodes: (codeChange: Accelerate.CodeChange) => {},
    createTransactionAndAddCode: (
      searchSelection: CMxCommonApp.SearchResult
    ) => {},
    onTransactionSharedDiagnosisChange: (
      changeDetail: CMxCommonApp.TransactionSharedCodeChange
    ) => {},
    onFeatureSelection: (feature: CMxCommonApp.ChargeSessionSelection) => {},
    onInvoiceMetaEdit: (invoice: AmplifyAPI.Invoice) => {},
    onInvoiceRemove: (
      invoiceDeleteParams: Accelerate.InvoiceDeleteParams
    ) => {},
    onChargeSessionAction: (
      transactionAction: CMxCommonApp.TransactionAction
    ) => {},
    handleEnableTechPro: (
      transactionCodeChange: CMxCommonApp.TechCodableTransaction
    ) => {},
    handleDisableTechPro: (
      transactionCodeChange: CMxCommonApp.TechCodableTransaction
    ) => {},
    codableInvoices: [],
    searchResults: {},
    codeSearchResultsV2: {},
    editable: true,
    panelActivations: {},
    codeEdits: {},
    counts: {
      procedures: 0,
      diagnosis: 0,
      modifiers: 0
    },
    suggestedCodes: { ...DEFAULT_SUGGESTED_CODES }
  };

  constructor(props: CodeBenchPanelProps) {
    super(props);
    this.state = {
      editState: {
        add: false
      }
    };
  }

  /**
   * Event that happen when Enter key is pressed on  add Invoice link
   * @returns {void}
   */

  handleKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.props.handleInvoiceAddition();
    }
  };

  createItemsForTabContainer = () => {
    const {
      codableInvoices,
      searchResults,
      codeSearchResultsV2,
      codeEdits,
      onChargeSessionAction,
      panelActivations,
      processInstance,
      editable,
      suggestedCodes,
      onFeatureSelection,
      onCodeSearch,
      onCodeSearchSelection,
      handleAddAllCodes,
      handleRemoveAllCodes,
      onCodeUpdate,
      onCodeRemove,
      onTimeBasedCodeAdd,
      onTimeBasedCodeUpdate,
      onCodeSearchDrop,
      onTransactionSharedDiagnosisChange,
      handleEnableTechPro,
      handleDisableTechPro,
      onInvoiceMetaEdit,
      onInvoiceRemove,
      onTransactionOrderChange
    } = this.props;
    let counter = 0; // Initialize counter

    const handleInvoiceRemove = (invoiceGuid: string) => {
      this.props.onInvoiceRemove({
        processInstance,
        invoiceGuid: invoiceGuid
      });
    };

    const isEmptyCPTCodeTransaction = (
      transaction: CMxCommonApp.CodableTransaction
    ): boolean => {
      const hasProcedureCode = transaction.codes.some(
        (code) => 'codeType' in code && code.codeType === 'procedure'
      );
      return !hasProcedureCode;
    };

    let firstEmptyCPTTransactionFound = false; // Tracks if an empty transaction has been found
    const items = codableInvoices.map((codableInvoice, idx) => {
      const providerName = codableInvoice.billing_provider_name ?? '';
      const performingName = codableInvoice.performing_provider_name ?? '';
      const referringName = codableInvoice.referring_provider_name ?? '';
      const billArea = codableInvoice.fee_schedule ?? 'None';
      const department = codableInvoice?.department?.name ?? '';
      const dateOfService = codableInvoice.date_of_service
        ? cmxDateTime.format(
            codableInvoice.date_of_service,
            cmxDateTime.FORMATS.DATE_OF_SERVICE
          )
        : 'Date not selected';

      /**
       * Event triggered when a search selection is pressed to create a transaction and add code
       * @param ev
       */
      const createTransactionAndAddCode = (
        searchResult: Accelerate.CodeActionPayload
      ) => {
        const searchSelection = {
          ...searchResult,
          context: {
            invoice: { ...codableInvoice },
            processInstance: { ...this.props.processInstance }
          }
        };
        this.props.createTransactionAndAddCode(searchSelection);
      };

      const txns = codableInvoice.transactions.map(
        (txn: CMxCommonApp.CodableTransaction, txnIdx) => {
          const rawResults = searchResults[txn.guid] ?? [];
          const rawResultsV2 = codeSearchResultsV2[txn.guid] ?? {};
          if (codableInvoice.type !== commonEnums.InvoiceTypes.SHARED_CODES) {
            counter++;
          }
          let shouldAutoFocus = false;
          // to find first transaction with no CPT code.
          if (
            codableInvoice.type !== 'SHARED_CODES' &&
            isEmptyCPTCodeTransaction(txn) &&
            !firstEmptyCPTTransactionFound
          ) {
            firstEmptyCPTTransactionFound = true;
            shouldAutoFocus = true;
          }

          const TransactionComponent = this.props.transactionV2
            ? TransactionV2
            : Transaction;
          const transactionProps = {
            key: `txn-${txn.guid}`,
            codeEdits,
            onChargeSessionAction,
            panelActivations,
            invoice: codableInvoice,
            processInstance,
            autoFocus: shouldAutoFocus,
            editable,
            onFeatureSelection,
            searchResults: rawResults,
            codeSearchResultsV2: rawResultsV2,
            onCodeSearch,
            onCodeSearchSelection,
            handleAddAllCodes,
            handleRemoveAllCodes,
            createTransactionAndAddCode,
            handleCodeChange: onCodeUpdate,
            handleCodeRemove: onCodeRemove,
            handleTransactionOrderChange: onTransactionOrderChange,
            handleTimeBasedCodeAdd: onTimeBasedCodeAdd,
            handleTimeBasedCodeUpdate: onTimeBasedCodeUpdate,
            handleCodeSearchDrop: onCodeSearchDrop,
            onTransactionSharedDiagnosisChange,
            lineNumber: counter,
            codableTransaction: txn,
            suggestedCodes,
            handleEnableTechPro,
            handleDisableTechPro,
            scrollContainerRef: this.tabContainerRef,
            transactionIndex: txnIdx
          };

          return <TransactionComponent {...transactionProps} />;
        }
      );

      /**
       * Event launched when a search is performed
       * @param ev
       */
      const onSearch = (codeQuery: CMxCommonApp.CodeQuery) => {
        this.props.onCodeSearch({
          ...codeQuery,
          context: {
            invoice: { ...codableInvoice },
            processInstance: { ...this.props.processInstance }
          }
        });
      };

      const invoiceType =
        codableInvoice?.type?.toLowerCase().replace(/_/g, '-') ?? 'unknown';
      const additionalInvoiceCardClasses = [
        'codable-invoice',
        'qa-codable-invoice',
        `invoice-${invoiceType}`
      ];
      //`invoice-${invoiceType}`
      const invoiceBody = (
        <InvoiceCard
          additionalClasses={additionalInvoiceCardClasses}
          key={`invoice-${idx}`}>
          <div className="invoice-meta">
            <div className="invoice-meta-line invoice-meta-provider">
              {editable && (
                <ErrorSubmission
                  errorMessages={codableInvoice?.errorMessages}
                  messageStyle="inv-error-message"
                  messageType="error"
                />
              )}
              <InvoiceMetadatav2
                invoice={codableInvoice}
                onInvoiceMetaEdit={onInvoiceMetaEdit}
                fields={InvoiceMetadataFormFields}
                editable={editable}
                onInvoiceRemove={handleInvoiceRemove}
              />
            </div>
          </div>
          {txns}
          <Conditional
            every={[editable, !invoiceType.includes('shared')]}
            displayMode="flex">
            <div className="add-transaction-with-code-section">
              <SuggestedLookup
                actionPrefix=""
                placeholderText="Add procedure code"
                debounceTimeout={200}
                FieldDefinitions={FieldDefinitions.filter(
                  (def) => def.label.toLowerCase() === 'procedure'
                )}
                shouldShowResults={shouldShowResults}
                autoFocus={false}
                onSearch={onSearch}
                onSearchSelection={createTransactionAndAddCode}
                searchResults={
                  codeSearchResultsV2?.[codableInvoice?.guid]?.[
                    CodeSearchType.CPT
                  ] ?? []
                }
                searchResultsLookupKey={codableInvoice?.guid}
                showSearchButton={false}
                actionNameForDD="~Focus code search (General)"
                suggestedCodes={suggestedCodes}
                allowCodeRemove={false}
              />
              <CMXIcon
                svgIconName="BiomeChevronDownSVG20"
                svgIconSize="16"
                svgIconClass="select-chevron"
              />
            </div>
          </Conditional>
        </InvoiceCard>
      );

      return {
        headerText: dateOfService,
        subheaderText: providerName,
        body: invoiceBody
      };
    });
    return { items, counter };
  };

  parseDate(dateString) {
    const [month, day, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  sortItems(itemsToSort) {
    return itemsToSort.sort((a, b) => {
      const dateA = this.parseDate(a.headerText);
      const dateB = this.parseDate(b.headerText);

      // Compare the dates
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;

      // Compare the provider names
      const providerA = a.subheaderText.toLowerCase();
      const providerB = b.subheaderText.toLowerCase();

      if (providerA < providerB) return -1;
      if (providerA > providerB) return 1;

      return 0;
    });
  }

  render() {
    const {
      searchResults,
      counts,
      editable,
      benchEditable,
      benchLoading,
      onCodeSearch,
      onCodeSearchSelection,
      handleAddAllCodes,
      handleRemoveAllCodes,
      onCodeUpdate,
      onCodeRemove,
      onTimeBasedCodeAdd,
      onTimeBasedCodeUpdate,
      onCodeSearchDrop,
      onFeatureSelection,
      onInvoiceMetaEdit: onChargeSessionMetaEdit,
      onChargeSessionAction,
      onTransactionSharedDiagnosisChange,
      panelActivations,
      codableInvoices,
      processInstance,
      codeEdits,
      showProcessStatus,
      caseDetailProps,
      handleEnableTechPro,
      handleDisableTechPro,
      customClaimsRelatedCases,
      customClaimsRelatedCasesLoadState,
      openChildWindow,
      // Consume bench-only props here so they are not forwarded to the root <div> via ...rest
      suggestedCodes: _suggestedCodes,
      onTransactionOrderChange: _onTransactionOrderChange,
      codeSearchResultsV2: _codeSearchResultsV2,
      transactionV2: _transactionV2,
      createTransactionAndAddCode: _createTransactionAndAddCode,
      onInvoiceRemove: _onInvoiceRemove,
      handleInvoiceAddition,
      summarySection,
      ...rest
    } = this.props;
    const styleClasses = ['code-bench-panelv2'];

    const { items, counter } = this.createItemsForTabContainer();

    const noInvoices = (
      <div className="no-transactions">
        <div className="empty-message editable">
          This case does not have any charges. Add a service date and providers
          with the charge button below, then add procedures to the set.
        </div>
        <Conditional on={editable}>
          <div
            className="add-element-div"
            tabIndex={0}
            onKeyDown={this.handleKeyDown}
            onClick={handleInvoiceAddition}>
            <CMXIcon svgIconName="BiomePlusCircle" svgIconSize="20" />
            <a className="add-element-button">Add invoice</a>
          </div>
        </Conditional>
      </div>
    );

    return (
      <div {...rest} className={classnames(styleClasses)}>
        {showProcessStatus && (
          <div className="status-section process-status-section">
            <ProcessStatus benchEditable={benchEditable} />
          </div>
        )}

        {customClaimsRelatedCasesLoadState?.loading ? (
          <LoadingSlugs numberItems={5} />
        ) : (
          processInstance?.errorMessages?.length > 0 && (
            <ErrorSubmission
              errorMessages={processInstance.errorMessages}
              messageHeader={
                benchEditable.submitted
                  ? 'We have found the following errors:'
                  : 'We have found the following that reduce the coding quality of this case:'
              }
              customClaimsRelatedProps={{
                cases: customClaimsRelatedCases,
                onOpenRelatedCase: this.props.openChildWindow,
                applyErrorTextStyle: !benchEditable.submitted
              }}
              messageStyle="case-error-message"
              messageType={benchEditable.submitted ? 'warning' : 'error'}
            />
          )
        )}

        {counter === 0 ? (
          noInvoices
        ) : items.length > 1 ? (
          <InteractiveTabContainer
            key={codableInvoices.length}
            items={this.sortItems(items.slice(1))}
            alwaysFirstElement={items[0].body}
            {...(editable && {
              action: {
                label: 'Add',
                onAction: handleInvoiceAddition
              }
            })}
            footerElement={summarySection}
            scrollContainerRef={this.tabContainerRef}
          />
        ) : (
          <InteractiveTabContainer key={codableInvoices.length} items={items} />
        )}
      </div>
    );
  }
}

export { CodeBenchPanelv2 };
